import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { LuArrowUp, LuPaperclip, LuX } from "react-icons/lu";
import AttachmentChip from "./AttachmentChip";

const MAX_ATTACHMENTS = 6;

function cx(...classes) {
    return classes.filter(Boolean).join(" ");
}

/**
 * Prompt box with attachments. Owns its text and files; calls onSend(content, files).
 * The parent can prefill text through the ref: ref.current.setText("...").
 */
/**
 * variant "hero": roomy box with the controls underneath (empty chat, home page).
 * variant "dock": one compact row that sits over the conversation.
 */
const Composer = forwardRef(function Composer({ disabled, error, onSend, onDismissError, variant = "hero" }, ref) {
    const dock = variant === "dock";
    const [input, setInput] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [attachmentError, setAttachmentError] = useState(null);
    const [focused, setFocused] = useState(false);
    const [dragging, setDragging] = useState(false);
    const fileInputRef = useRef(null);
    const textareaRef = useRef(null);
    const dragDepth = useRef(0);
    const attachmentsRef = useRef(attachments);

    useImperativeHandle(ref, () => ({
        setText(text) {
            setInput(text);
            textareaRef.current?.focus();
        },
    }));

    useEffect(() => {
        attachmentsRef.current = attachments;
    }, [attachments]);

    useEffect(() => () => attachmentsRef.current.forEach((a) => URL.revokeObjectURL(a.previewUrl)), []);

    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "0px";
        el.style.height = `${Math.min(Math.max(el.scrollHeight, dock ? 24 : 52), 160)}px`;
    }, [input, dock]);

    const canSend = (input.trim().length > 0 || attachments.length > 0) && !disabled;

    const addFiles = (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        const accepted = files.filter((f) => f.type.startsWith("image/"));
        setAttachmentError(accepted.length < files.length ? "Only images can be attached." : null);
        setAttachments((prev) => {
            const room = MAX_ATTACHMENTS - prev.length;
            if (accepted.length > room) setAttachmentError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
            const next = accepted.slice(0, Math.max(room, 0)).map((file) => ({
                key: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
                file,
                previewUrl: URL.createObjectURL(file),
                kind: file.type.startsWith("video/") ? "video" : "image",
            }));
            return [...prev, ...next];
        });
    };

    const removeAttachment = (key) => {
        setAttachments((prev) => {
            const target = prev.find((a) => a.key === key);
            if (target) URL.revokeObjectURL(target.previewUrl);
            return prev.filter((a) => a.key !== key);
        });
        setAttachmentError(null);
    };

    const handleSend = () => {
        const content = input.trim();
        if ((!content && attachments.length === 0) || disabled) return;
        // The receiver makes its own previews from the files.
        onSend(content, attachments.map((a) => a.file));
        attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl));
        setInput("");
        setAttachments([]);
        setAttachmentError(null);
    };

    const combinedError = attachmentError ?? error ?? null;

    const attachButton = (
        <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={attachments.length >= MAX_ATTACHMENTS}
            aria-label="Attach images"
            title="Attach images"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-primary-700 transition-colors hover:bg-primary-50 disabled:opacity-30"
        >
            <LuPaperclip size={17} />
        </button>
    );
    const sendButton = (
        <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            aria-label="Send message"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-700 text-white transition-colors hover:bg-primary-800 disabled:bg-primary-100 disabled:text-primary-800"
        >
            <LuArrowUp size={17} />
        </button>
    );
    const textarea = (
        <textarea
            ref={textareaRef}
            value={input}
            rows={dock ? 1 : 2}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                }
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onPaste={(e) => {
                const files = Array.from(e.clipboardData.files);
                if (files.length === 0) return;
                e.preventDefault();
                addFiles(files);
            }}
            placeholder={dock ? "Message TryOn AI" : "Describe what you want, or attach a photo"}
            className={cx(
                "agent-composer-input block w-full resize-none overflow-y-auto bg-transparent text-[15px] leading-6 text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)]",
                dock ? "py-2" : "px-4 pb-1 pt-3.5"
            )}
        />
    );

    return (
        <div className="w-full text-left">
            <div
                className={cx(
                    "relative border bg-white transition-[border-color,box-shadow] duration-150",
                    dock ? "rounded-[26px]" : "rounded-2xl",
                    focused || dragging
                        ? "border-primary-200 shadow-[var(--ring)]"
                        : "border-[var(--border-secondary)] hover:border-primary-200"
                )}
                onDragEnter={(e) => {
                    e.preventDefault();
                    dragDepth.current += 1;
                    setDragging(true);
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "copy";
                }}
                onDragLeave={(e) => {
                    e.preventDefault();
                    dragDepth.current = Math.max(dragDepth.current - 1, 0);
                    if (dragDepth.current === 0) setDragging(false);
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    dragDepth.current = 0;
                    setDragging(false);
                    addFiles(e.dataTransfer.files);
                }}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                        addFiles(e.target.files);
                        e.target.value = "";
                    }}
                />

                {attachments.length > 0 && (
                    <div className={cx("flex gap-2 overflow-x-auto pt-3", dock ? "px-4" : "px-3")}>
                        {attachments.map((a) => (
                            <AttachmentChip
                                key={a.key}
                                kind={a.kind}
                                name={a.file.name}
                                previewUrl={a.previewUrl}
                                onRemove={() => removeAttachment(a.key)}
                            />
                        ))}
                    </div>
                )}

                {dock ? (
                    <div className="flex items-end gap-1 px-2 py-1.5">
                        {attachButton}
                        <div className="min-w-0 flex-1">{textarea}</div>
                        {sendButton}
                    </div>
                ) : (
                    <>
                        {textarea}
                        <div className="flex items-center justify-between gap-2 px-2 pb-2 pt-1">
                            {attachButton}
                            {sendButton}
                        </div>
                    </>
                )}

                {dragging && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-[inherit] bg-white/90 text-sm text-[var(--text-secondary)]">
                        Drop images
                    </div>
                )}
            </div>

            {!combinedError && (
                <p className={cx("mt-2 font-Mulish text-xs text-graphite/70", dock ? "text-center" : "px-1")}>
                    Enter to send · Shift+Enter for a new line
                </p>
            )}

            {combinedError && (
                <div className="mt-2 flex items-center justify-between gap-2 rounded-xl bg-primary-50 px-3 py-2 font-Mulish text-xs text-primary-800">
                    <span>{combinedError}</span>
                    <button
                        type="button"
                        onClick={() => {
                            setAttachmentError(null);
                            onDismissError?.();
                        }}
                        aria-label="Dismiss error"
                        className="shrink-0 text-primary-700 hover:text-primary-800"
                    >
                        <LuX size={13} />
                    </button>
                </div>
            )}
        </div>
    );
});

export default Composer;
